import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Creators as paymentsActions } from '../../../../../domain/useCases/payments/getPendingPayments';
import { Creators as benefitActions } from '../../../../../domain/useCases/payments/getBenefitInstallments';
import { Creators as CreateOrderActions } from '../../../../../domain/useCases/createOrder';
import { Creators as PayOrderActions } from '../../../../../domain/useCases/payOrder';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';
import PayOnlineView from '@/presentation/modules/payments/modules/PayOnline/PayOnlineView';
import {
  formatDiscountBenefitItems,
  generateIziConfig
} from '@/presentation/modules/payments/modules/PayOnline/helpers';
import PaymentDetailsView from '../PaymentDetails/PaymentDetailsView';
import DateHelpers from '@/core/helpers/DateHelper';
import Spinner from '@/presentation/components/Spinner';
import { useRole } from '@/presentation/hooks/useRole';

export interface IPaymentItem {
  status: string;
  name: string;
  amount: string;
  recharge?: string;
  dueDate: number;
  itemsIds?: string[];
  periodDesc?: string;
  itemNumber: string;
  amountWithBenefits?: string;
  amountToPay: string;
  hasDiscountProntoPago?: boolean;
  paymentType: {
    id: string;
    desc: string;
  };
  billName?: string;
  discountProntoPago?: number;
  discountCicloCompleto?: number;
  discountBene?: number;
  dateCreate?: number;
  billingDate?: number;
}

const PayOnlineViewController = () => {
  const history = useHistory();
  const { isVisor } = useRole();
  const dispatch = useDispatch();
  const [errorModal, setErrorModal] = useState<string[]>(['']);
  const [restriction, setRestriction] = useState(false);
  const [currentPay, setCurrentPay] = useState(0);
  const [paysSelected, setPaysSelected] = useState<string[]>([]);
  const [itemsChecked, setItemsChecked] = useState<string[]>([]);
  const [responseDetail, setResponseDetail] = useState({
    response: {
      code: '',
      message: '',
      messageUser: '',
      response: {
        order: [{ dateTransaction: '20240201', orderNumber: '' }],
        card: { brand: '' },
        billing: { email: '' },
        payMethod: ''
      },
      transactionId: ''
    },
    time: '',
    payments: []
  });

  const { data: benefitData, isLoading: isLoadingBenefit } = useSelector(
    (state: any) => state.getBenefitInstallments
  );
  const { data: paymentsResults, isLoading: isLoadingGetPayments } =
    useSelector((state: any) => state.getPendingPayments);
  const {
    isLoading: isLoadingCreateOrder,
    isSuccess,
    data
  } = useSelector((state: any) => state.createOrder);
  const payOrder = useSelector((state: any) => state.payOrder);
  const studentCode = useSelector(
    (state: any) => state?.profile?.data?.profile?.studentCode
  );

  useEffect(() => {
    setResponseDetail({
      response: {
        code: '',
        message: '',
        messageUser: '',
        response: {
          order: [{ dateTransaction: '', orderNumber: '' }],
          card: { brand: '' },
          billing: { email: '' },
          payMethod: ''
        },
        transactionId: ''
      },
      time: '',
      payments: []
    });
    dispatch(benefitActions.getBenefitInstallments());
    dispatch(paymentsActions.getPendingPayments());

    return () => {
      dispatch(PayOrderActions.payOrderClear());
      dispatch(CreateOrderActions.createOrderClear());
    };
  }, []);

  const periodsPending: string[] = [];
  paymentsResults?.pendingPayments?.items.forEach((item: any) => {
    if (!periodsPending.includes(item.periodDesc))
      periodsPending.push(item.periodDesc);
  });

  const handleCheckboxChange = (checkbox: string) => {
    const prevItemsChecked = itemsChecked;
    const index = prevItemsChecked.indexOf(checkbox);
    if (index > -1) {
      const removed = prevItemsChecked.filter((_, i) => i >= index);
      setCurrentPay((prevCurrent) => prevCurrent - removed.length);
      setItemsChecked(prevItemsChecked.filter((_, i) => i < index));
    } else {
      setCurrentPay((prevCurrent) => prevCurrent + 1);
      prevItemsChecked.push(checkbox);
      setItemsChecked([...prevItemsChecked]);
    }
  };

  useEffect(() => {
    setPaysSelected([...itemsChecked]);
  }, [itemsChecked]);

  const itemsPayedData = () => {
    const joinedPaymentItems = paymentsResults.pendingPayments.items;
    if (paymentsResults.pendingPayments.discountPayment) {
      return [] as any;
    }
    return paysSelected.map((item) =>
      joinedPaymentItems.find((i: any) => item === i.itemNumber)
    );
  };

  const goBackButton = () => {
    GoogleAnalytics.writeLog('back-from-payonline', 'backbutton');
    history.push('/pagos/opciones-pago');
  };

  const goToPayments = () => {
    history.push('/pagos');
  };

  const items: IPaymentItem[] = useMemo(() => {
    if (!paymentsResults || !benefitData) return [];
    return formatDiscountBenefitItems(
      paymentsResults.pendingPayments,
      benefitData.requestBenefit
    );
  }, [paymentsResults, benefitData]);

  const regularPay = items
    .filter((item) => paysSelected.includes(item.itemNumber))
    .reduce((p, c) => p + Number(c.amountToPay ?? 0), 0);

  const totalPay = Number(regularPay.toFixed(2));

  const createOrder = () => {
    if (isVisor) {
      setRestriction(true);
      return;
    }
    let filteredPaysSelected = paysSelected;
    if (
      benefitData.requestBenefit.discountPayment &&
      filteredPaysSelected.find((p) => p === 'total')
    ) {
      const totalFeePay = items.find((i) => i.itemNumber === 'total');
      filteredPaysSelected.push(...(totalFeePay?.itemsIds ?? []));
    }
    // Filter all duplicates. Return only one of each code
    filteredPaysSelected = filteredPaysSelected.filter(
      (item: string, pos: number, self) => self.indexOf(item) === pos
    );

    dispatch(
      CreateOrderActions.createOrder(
        filteredPaysSelected.filter((f) => f !== 'total'),
        totalPay
      )
    );
    dispatch(PayOrderActions.payOrderClear());
  };

  const callbackIzipayResponse = (response: any) => {
    const time = DateHelpers.formatHour(new Date().getTime());
    const payments = itemsPayedData();
    setResponseDetail({ response, time, payments });
  };

  useEffect(() => {
    if (responseDetail?.response?.response?.order[0]?.orderNumber) {
      dispatch(
        PayOrderActions.payOrder(
          responseDetail?.response.code,
          responseDetail?.response.response.order[0].orderNumber,
          responseDetail?.response.transactionId,
          responseDetail?.response.response.billing.email,
          responseDetail?.response.response.payMethod,
          responseDetail?.response.response.card.brand,
          responseDetail?.response.messageUser ??
            responseDetail?.response.message
        )
      );
    }
  }, [responseDetail?.response?.code]);

  useEffect(() => {
    if (isSuccess && data) {
      const { transactionId, orderId, token, merchantCode, phone, campus } =
        data;
      generateIziConfig(
        totalPay,
        transactionId,
        orderId,
        token,
        merchantCode,
        phone,
        campus,
        studentCode,
        (response) => callbackIzipayResponse(response)
      );
    }
  }, [isSuccess, data]); // Viene del createOrder

  useEffect(() => {
    if (
      payOrder.isSuccess &&
      payOrder.data &&
      !payOrder.error &&
      !payOrder.isLoading
    ) {
      if (
        payOrder.data.message === 'Operación exitosa' ||
        payOrder.data.message === 'Aprobado'
      ) {
        setCurrentPay(0);
        setPaysSelected([]);
      }
    }
  }, [payOrder]);

  useEffect(() => {
    if (responseDetail) {
      if (responseDetail.response.code !== '00')
        if (responseDetail.response.code === '021') {
          setErrorModal([
            responseDetail.response.message,
            'Pago no completado'
          ]);
        } else
          setErrorModal([
            responseDetail.response.messageUser,
            'Tu pago fue rechazado'
          ]);
    }
  }, [responseDetail]);

  const goToPaymentHistory = () => {
    history.push('/pagos/historial');
  };

  return (
    <>
      {payOrder.isLoading ? (
        <Spinner />
      ) : (
        <>
          {payOrder.isSuccess && responseDetail.response.code === '00' ? (
            <PaymentDetailsView
              goToPayments={goToPayments}
              paymentDetail={responseDetail}
              discountPayment={paymentsResults?.payments?.discountPayment}
            />
          ) : (
            <PayOnlineView
              goBackButton={goBackButton}
              goToPaymentHistory={goToPaymentHistory}
              items={items}
              periodsPending={periodsPending}
              paysSelected={itemsChecked}
              currentPay={currentPay}
              handleCheckboxChange={handleCheckboxChange}
              totalPay={totalPay}
              isLoadingPending={isLoadingGetPayments}
              isLoadingBenefit={isLoadingBenefit}
              createOrder={createOrder}
              isLoadingCreateOrder={isLoadingCreateOrder}
              errorMessage={errorModal}
              setErrorMessage={setErrorModal}
              restriction={restriction}
              setRestriction={setRestriction}
            />
          )}
        </>
      )}
    </>
  );
};

export default PayOnlineViewController;
